/**
 * Welcome screen for the Upright chat — shown on empty threads.
 *
 * Inspired by the Upright Signal concept: prominent title, context facts,
 * pill suggestions. The suggestion chips are now generated by
 * `welcome-suggestions.ts` based on a richer context snapshot than
 * before — entity type (company / group / portfolio), entity name, the
 * lens the user came from (Net impact, Double materiality, Financial
 * effects, …), and any detail label like a DMA matter title. The chips
 * also gently rotate day-by-day so the welcome doesn't feel like the
 * same four strings forever.
 *
 * The chat-context chip (`<ChatContextCard />`, PLT-4701 follow-up) is
 * NOT rendered here. It lives one level up in `<UprightChatInner>` above
 * the `<Thread>` so it stays visible in both the welcome and the active
 * conversation states (i.e. doesn't disappear the moment the user sends
 * a message). The welcome's headline + suggestion pills sit below the
 * chip in the visual hierarchy, but the chip itself isn't part of this
 * subtree.
 */
import { ThreadWelcome } from '@assistant-ui/react-ui';
import { useAssistantRuntime } from '@assistant-ui/react';
import { useSyncExternalStore } from 'react';
import {
  composerState,
  getPageChatContext,
  getSeedingInitialPrompt,
  subscribePageChatContext,
  subscribeSeedingInitialPrompt,
} from './upright-chat-shared';
import { getStickyPageChatBinding, subscribeStickyPageChatBinding } from './current-page-chat-binding';
import { getStickyPageChatPathContext, subscribeStickyPageChatPathContext } from './current-page-chat-path-context';
import { buildWelcomeSuggestions, WELCOME_SUGGESTIONS_STATIC, type WelcomeSuggestion } from './welcome-suggestions';
import { useChatSurface } from './chat-surface';
import { ChatConnectorEntry } from './chat-connector-entry';

/**
 * Welcome suggestion pills that track source for usage logging.
 *
 * Rendered as a real `<a href>` so the browser honors cmd / ctrl / middle-click
 * → "open in new tab". On a plain primary-button click we `preventDefault()`
 * and reuse the existing in-place flow (`runtime.thread.append`); modifier
 * clicks fall through to native navigation, landing on `/chat?prompt=…&source=
 * welcome_suggestion`. The chat view reads `?prompt=…` and auto-submits, and
 * `?source=…` is forwarded to the adapter via `composerState.pendingSource`
 * so a cmd-clicked tab still reports the welcome path in usage analytics
 * (instead of silently downgrading to `user_input`).
 *
 * NOTE: Using a bare `<a>` rather than the Next.js / router `<Link>` is
 * deliberate. `<Link>` hijacks plain clicks for client-side nav and would
 * break the in-place `runtime.thread.append` flow we still want for normal
 * clicks. The bare anchor + manual `preventDefault` is the simplest thing
 * that preserves both behaviors.
 */
function WelcomeSuggestionWithTracking({ text, prompt }: { text: string; prompt: string }) {
  const runtime = useAssistantRuntime();
  // The welcome screen is still mounted and clickable through the whole await in
  // `InitialPromptAutoSender`, and `switchToNewThread` does not abort the
  // outgoing run. A pill clicked in that window appends a second prompt that
  // runs to completion alongside the deep-linked one, under a stale `threadId`
  // (PLT-6093 / PLT-6108). Thread state cannot express that window — the pills
  // render only on an empty thread, and an empty thread is never `isRunning` —
  // so the auto-sender publishes it instead.
  const seeding = useSyncExternalStore(subscribeSeedingInitialPrompt, getSeedingInitialPrompt, () => false);
  const href = `/chat?prompt=${encodeURIComponent(prompt)}&source=welcome_suggestion`;
  return (
    <a
      href={href}
      className="aui-thread-welcome-suggestion"
      aria-disabled={seeding}
      onClick={e => {
        // Only intercept plain primary-button clicks. Anything with a
        // modifier (cmd/ctrl/shift/alt) or middle-click falls through to
        // native browser navigation → opens in a new tab/window.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        // An anchor cannot carry `disabled`, so the guard is here.
        if (seeding) return;
        composerState.pendingSource = 'welcome_suggestion';
        runtime.thread.append(prompt);
      }}
    >
      <span className="aui-thread-welcome-suggestion-text">{text}</span>
    </a>
  );
}

/**
 * Static `WELCOME_SUGGESTIONS` export retained for the Thread's
 * `welcome.suggestions` config (`upright-chat.tsx`), which expects a
 * plain array. Our custom `UprightChatWelcome` component overrides this
 * at render time with the dynamic generator output; the array is the
 * last-resort fallback for when our custom welcome isn't mounted.
 */
export const WELCOME_SUGGESTIONS = WELCOME_SUGGESTIONS_STATIC;

/**
 * Welcome message that adapts to page context. Keeps the generic
 * "What do you want to analyze?" copy on standalone /chat loads (no
 * page context at all), and switches to an entity-aware question only
 * when we have resolved entity framing — i.e. both a name and a
 * recognised entity type, the same condition under which the suggestion
 * chips switch to entity-specific framing. Coupling the two on one
 * `resolvedEntityName` guarantees the headline and the chips agree:
 * PLT-4938 was a company-scoped headline sitting over generic chips,
 * which happened precisely because the headline resolved a name the
 * chips couldn't.
 *
 * `hasPageContext` keeps the softer "What would you like to know?"
 * intermediate copy for the (degenerate) case where the chat was opened
 * from a page but we couldn't resolve entity framing — still better than
 * the bare standalone prompt, and never names an entity the chips ignore.
 */
function getWelcomeMessage(resolvedEntityName: string | null, hasPageContext: boolean): string {
  if (resolvedEntityName !== null) {
    return `What would you like to know about ${resolvedEntityName}?`;
  }
  if (hasPageContext) return 'What would you like to know?';
  return 'What do you want to analyze?';
}

/**
 * Resolve the entity-type input for the suggestion generator. The
 * sticky binding's `entityType` is published as a free-form string
 * (`'company'` / `'group'` / `'portfolio'`) by the entity views; we
 * narrow it here to the union the generator expects, falling through
 * to `null` for anything unexpected so a typo upstream doesn't crash
 * the welcome screen.
 *
 * `'supplier'` (PLT-6269) never arrives from a sticky binding — no entity view
 * publishes it, because a supplier has no page of its own. It comes only from a
 * page-context chip whose producer asked for supplier framing, and it means "a
 * company, seen by its buyer"; `welcome-suggestions.ts`'s `entityType` doc has
 * why that is a framing rather than a fifth entity type.
 */
function narrowEntityType(raw: string | null | undefined): 'company' | 'group' | 'portfolio' | 'supplier' | null {
  if (raw === 'company' || raw === 'group' || raw === 'portfolio' || raw === 'supplier') return raw;
  return null;
}

/**
 * Custom welcome screen — replaces the default assistant-ui welcome.
 *
 * Suggestion pill layout (PLT-4605): the first three (short) pills sit
 * on one row; the last (long "Screen tech companies…") pill goes on its
 * own row below. Done via a 100%-basis flex wrapper around the last
 * pill — see `.aui-thread-welcome-suggestion-row` in
 * `upright-chat-theme.css`. We apply that split only to the standalone
 * `/chat` welcome (where the generator emits a long trailing pill on
 * purpose); contextual sets are all short and fit on the normal flex
 * row.
 *
 * "Whole-chat context" surfacing — the bound entity and its href —
 * happens above the thread inside `UprightChatInner` via
 * `ChatContextCard` (PLT-4701). The welcome screen no longer carries its
 * own chip; the card is persistent across the full chat lifetime, not
 * just the empty welcome state.
 */
export function UprightChatWelcome() {
  // Three subscriptions, each backed by an existing module-level store.
  // We don't combine them into one snapshot because each store changes
  // independently (the page-context chip can be cleared before the
  // sticky binding/path context goes stale, and vice versa).
  const ctx = useSyncExternalStore(subscribePageChatContext, getPageChatContext, () => null);
  const binding = useSyncExternalStore(subscribeStickyPageChatBinding, getStickyPageChatBinding, () => null);
  const pathContext = useSyncExternalStore(
    subscribeStickyPageChatPathContext,
    getStickyPageChatPathContext,
    () => null,
  );

  // Resolve the entity name / type / lens from a single combined
  // snapshot, so the headline and the suggestion chips can never disagree
  // (PLT-4938). For each axis we prefer the in-page sticky publisher — the
  // binding (published by a mounted entity view) and the path context
  // (published by a mounted sub-route) — and fall back to the page-context
  // chip, which is the only signal present on entry points that set page
  // context without mounting an entity view (e.g. the dimension-bar seed
  // that produced the bug: it sets `pageChatContext` but no `CompanyView`
  // mounts to publish a binding).
  //   - name : binding label (just the name, e.g. "Neste") → split the
  //            chip's " — "-joined label.
  //   - type : binding entityType → page-context entityType (backfilled
  //            from the seed in `chat-side-panel.tsx`).
  //   - lens : path-context lensLabel → page-context lensLabel (set by the
  //            net-impact seeds that know their lens).
  //
  // **One exception inverts that order: a supplier** (PLT-6269). The suppliers
  // list is one company's page showing *another* company's assessment, so both
  // sticky publishers on it belong to the buyer — the page's own `CompanyView`
  // publishes the buyer as the binding, and the DMA sub-route publishes
  // `'Double materiality'` as the lens. Preferring them here would put the
  // buyer's name and the buyer's lens over the supplier's chips, which is
  // PLT-4938 with the two sides swapped. The supplier reaches us on the
  // page-context chip alone (seeded by `supplier-results-panel.tsx`'s "Continue
  // in chat"), so when that chip asks for supplier framing it wins outright on
  // every axis: it is the more specific signal, and the only one that is about
  // the entity the reader actually asked about.
  //
  // It is also the difference between working and working *by luck*: today the
  // supplier only wins because `resetChatLoggingState()` clears both sticky
  // stores an instant before the seed is applied, so they happen to be empty
  // when this renders. Either producer re-publishing — a re-render, a route
  // change under the open panel — would otherwise shadow the supplier entirely.
  const supplierCtx = ctx !== null && ctx.entityType === 'supplier' ? ctx : null;
  const ctxName = ctx?.label.split(' — ')[0]?.trim() ?? null;
  const entityName = supplierCtx !== null ? ctxName : (binding?.label ?? ctxName);
  const entityType = narrowEntityType(supplierCtx?.entityType ?? binding?.entityType ?? ctx?.entityType ?? null);
  const lensLabel = supplierCtx?.lensLabel ?? pathContext?.lensLabel ?? ctx?.lensLabel ?? null;
  // No detail label in the supplier case — the only publisher of one is the
  // buyer's own sub-route, so anything here would qualify the wrong entity.
  const detailLabel = supplierCtx !== null ? null : (pathContext?.detailLabel ?? null);

  // Full-page /chat vs. side panel. Only the narrow side panel needs the
  // suggestion-chip character cap; the full-page landing lets CSS size chips
  // to fit their single line, so capping there cut labels that had room to
  // show in full (PLT-5801).
  const layout = useChatSurface();

  // Entity framing applies only when we have BOTH a name and a recognised
  // type — that is exactly the condition under which `buildWelcomeSuggestions`
  // emits entity-specific chips rather than the generic "explore" set. The
  // headline keys off the same condition so the two always match.
  const resolvedEntityName = entityName && entityName.length > 0 && entityType !== null ? entityName : null;
  const message = getWelcomeMessage(resolvedEntityName, ctx !== null);

  const suggestions: WelcomeSuggestion[] = buildWelcomeSuggestions({
    entityName,
    entityType,
    lensLabel,
    detailLabel,
    layout,
  });

  // PLT-4605's "long pill on its own row" treatment is only meaningful for
  // the generic "explore" set, where the generator deliberately emits a long
  // trailing pill (e.g. "Screen tech companies by sustainability"). That set
  // renders exactly when there is no entity framing; entity-bound sets are
  // all short and fit on the normal flex row, so we render them without the
  // row split. The "explore the platform" stats block follows the same
  // condition so it appears with — and only with — the explore chips.
  const showExploreLayout = resolvedEntityName === null;
  const shortSuggestions = showExploreLayout ? suggestions.slice(0, -1) : suggestions;
  const longSuggestion = showExploreLayout ? suggestions[suggestions.length - 1] : undefined;

  return (
    <ThreadWelcome.Root>
      <ThreadWelcome.Center>
        {/*
         * Note: the chat-context chip is rendered one level up, in
         * `<UprightChatInner>` above the Thread. It used to sit here at
         * the top of the welcome cluster, but moving it out lets the
         * chip stay visible in the active conversation as well — not
         * just the empty welcome state.
         */}
        {/*
         * Render our own headline rather than `<ThreadWelcome.Message />`
         * so the copy can react to `pageChatContext`. The Thread's
         * `welcome.message` prop still feeds `<ThreadWelcome.Message />`
         * on standalone `/chat` mounts where this custom component
         * isn't rendered, so the two paths don't drift visibly.
         */}
        <p className="aui-thread-welcome-message">{message}</p>
        {showExploreLayout && (
          <div className="upright-welcome-context">
            <span>57 000 companies · 40 000 funds · 150 000 products</span>
            <span>Impact · Double materiality · Sustainability risks &amp; opportunities</span>
          </div>
        )}
        <div className="aui-thread-welcome-suggestions">
          {shortSuggestions.map(s => (
            <WelcomeSuggestionWithTracking key={s.prompt} text={s.text} prompt={s.prompt} />
          ))}
          {longSuggestion && (
            <div className="aui-thread-welcome-suggestion-row">
              <WelcomeSuggestionWithTracking text={longSuggestion.text} prompt={longSuggestion.prompt} />
            </div>
          )}
        </div>
        {/*
         * Side panel only (PLT-5889). On the full-page `/chat` route the same
         * connector entry point is pinned in the history sidebar's footer, and
         * the side panel has no sidebar — so this covers the users who only
         * ever see the panel, without doubling the affordance for the ones who
         * see both.
         */}
        {layout === 'side-panel' && (
          <div className="upright-welcome-connector">
            <ChatConnectorEntry variant="side-panel" />
          </div>
        )}
      </ThreadWelcome.Center>
    </ThreadWelcome.Root>
  );
}
